// src/pages/components/DueDiligenceChecklist.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/questionnareform.css"; // same base styles as the other forms
import "../css/duediligence.css"; // your ddq-specific tweaks
import { DDQ_SECTIONS } from "../../config/questionSchemas";
import { request } from "../../services/server";

// --------------------------------------------------------
// Constants (fix #12 — outside render)
// --------------------------------------------------------

const CONFIDENCE_LABEL = { high: "Hög", medium: "Medium", low: "Låg" };
const DDQ_STATUS_LABEL = { ok: "Finns / klar", na: "Ej tillgängligt", todo: "Pågår" };

// --------------------------------------------------------
// API helpers
// --------------------------------------------------------

function uploadUrl(workspaceKey) {
  return `/users/workspaces/${encodeURIComponent(workspaceKey)}/dataroom/upload/`;
}

function defaultQuizzesUrl(orgnr) {
  return `/valuation/workspace/${encodeURIComponent(orgnr)}/quizzes/`;
}

// --------------------------------------------------------
// Main component
// --------------------------------------------------------

export default function DueDiligenceChecklist({
  company, // workspace/orgnr
  storageKey,
  onSummaryChange,
  quizzesUrl,
}) {
  const effectiveQuizzesUrl = quizzesUrl || defaultQuizzesUrl(company);
  const [state, setState] = useState(() => loadDdqFromStorage(storageKey, company));

  const [loadingFromBackend, setLoadingFromBackend] = useState(false);
  const [backendError, setBackendError] = useState("");

  // upload state
  const [uploadErrByQid, setUploadErrByQid] = useState({});
  const [uploadingByQid, setUploadingByQid] = useState({});

  // Auto-save status — shown in the header so the user knows their changes
  // are being persisted (there is no manual "save" button).
  // shape: { state: "idle"|"saving"|"saved"|"error", at: Date|null, msg?: string }
  const [saveStatus, setSaveStatus] = useState({ state: "idle", at: null });
  const inflightSavesRef = useRef(0);
  const markSaving = () => {
    inflightSavesRef.current += 1;
    setSaveStatus((s) => ({ ...s, state: "saving" }));
  };
  const markSaveDone = (ok, msg) => {
    inflightSavesRef.current = Math.max(0, inflightSavesRef.current - 1);
    if (inflightSavesRef.current > 0) return; // still other saves in flight
    setSaveStatus(
      ok
        ? { state: "saved", at: new Date(), msg: undefined }
        : { state: "error", at: new Date(), msg: msg || "Kunde inte spara." }
    );
  };

  // AI autofill state: { [qid]: { loading, suggestion } }
  // suggestion = { status, notes, confidence, sources, doc_count }
  const [aiByQid, setAiByQid] = useState({});
  const abortUploadRef = useRef({}); // per-qid AbortController

  // save debounce timers (notes typing etc.)
  const saveTimerRef = useRef({}); // { [qid]: number }

  // Quick lookup map: qid -> question object
  const qById = useMemo(() => {
    const map = {};
    DDQ_SECTIONS.forEach((sec) => {
      (sec.items || []).forEach((q) => {
        map[q.qid] = q;
      });
    });
    return map;
  }, []);

  // --------------------------------------------------------
  // 1) Load existing DDQ from backend (source of truth)
  // --------------------------------------------------------
  useEffect(() => {
    if (!company) return;

    let alive = true;
    const ctrl = new AbortController();

    setLoadingFromBackend(true);
    setBackendError("");

    request(effectiveQuizzesUrl, { signal: ctrl.signal })
      .then((bundle) => {
        if (!alive) return;

        const ddq = bundle?.ddq || null;
        const answers = ddq?.answers && typeof ddq.answers === "object" ? ddq.answers : {};

        // Normalize backend answers to our UI state shape:
        // backend: attachments is typically [docId, ...]
        // UI: attachments is [{docId, name?, url?, size?}, ...] OR {docId} stubs
        const merged = mergeBackendAnswersIntoUiState(
          createInitialDdqState(company),
          answers
        );

        setState((prev) => {
          // If you have local edits cached, merge them on top.
          // Backend remains “truth”, but we keep UI stable.
          const prevSafe = prev && typeof prev === "object" ? prev : createInitialDdqState(company);
          return {
            ...merged,
            // keep any local attachment metadata (name/url/size) if present
            answers: mergeUiAnswersPreferRich(prevSafe.answers, merged.answers),
          };
        });
      })
      .catch((err) => {
        if (!alive) return;
        setBackendError(err?.body?.detail || "Kunde inte hämta DDQ-data från servern.");
      })
      .finally(() => {
        if (alive) setLoadingFromBackend(false);
      });

    return () => {
      alive = false;
      try {
        ctrl.abort();
      } catch {}
    };
  }, [company]);

  // --------------------------------------------------------
  // Keep local cache + update summary
  // --------------------------------------------------------
  useEffect(() => {
    saveDdqToStorage(storageKey, state);
    if (onSummaryChange) onSummaryChange(buildDdqSummary(state));
  }, [state, storageKey, onSummaryChange]);

  // --------------------------------------------------------
  // 2) Persist DDQ patch to backend
  // --------------------------------------------------------
  const postDdqPatch = async (qid, patch) => {
    if (!company) return;

    // Only send allowed keys to backend model
    const allowed = {};
    if (patch && typeof patch === "object") {
      if (patch.status !== undefined) allowed.status = patch.status;
      if (patch.notes !== undefined) allowed.notes = patch.notes;

      // attachments MUST be [docId,...] for backend
      if (patch.attachments !== undefined) {
        allowed.attachments = normalizeAttachmentsToDocIds(patch.attachments);
      }
    }

    markSaving();
    try {
      await request(effectiveQuizzesUrl, {
        method: "POST",
        body: {
          ddq: {
            mode: "merge",
            answers: {
              [qid]: allowed,
            },
          },
        },
      });
      markSaveDone(true);
    } catch (err) {
      markSaveDone(false, err?.body?.detail || err?.message);
      throw err;
    }
  };

  // Debounced save (especially for notes typing)
  const postDdqPatchDebounced = (qid, patch, delayMs = 600) => {
    // clear existing timer
    try {
      if (saveTimerRef.current[qid]) clearTimeout(saveTimerRef.current[qid]);
    } catch {}

    saveTimerRef.current[qid] = setTimeout(() => {
      // postDdqPatch already updates saveStatus on success/failure, so we
      // just swallow the rejection here (markSaveDone has already fired).
      postDdqPatch(qid, patch).catch(() => {});
    }, delayMs);
  };

  // --------------------------------------------------------
  // Handle status/notes changes: update state + post patch
  // --------------------------------------------------------
  const handleQuestionChange = (qid, patch) => {
    setState((prev) => {
      const current = prev.answers[qid] || emptyAnswer(qid);
      const next = { ...current, ...patch };

      return {
        ...prev,
        answers: {
          ...prev.answers,
          [qid]: next,
        },
      };
    });

    // Notes should be debounced; status can be instant
    if (patch && Object.prototype.hasOwnProperty.call(patch, "notes")) {
      postDdqPatchDebounced(qid, patch, 700);
    } else {
      postDdqPatch(qid, patch).catch(() => {});
    }
  };

  // --------------------------------------------------------
  // Upload files: upload to dataroom folder + store doc refs in DDQ answer
  // --------------------------------------------------------
  const handleFilesAdd = async (qid, files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;

    if (!company) {
      setUploadErrByQid((prev) => ({
        ...prev,
        [qid]: "Saknar organisationsnummer/workspace. Kan inte ladda upp.",
      }));
      return;
    }

    const q = qById[qid] || {};
    const folderPath = Array.isArray(q.folderPath) ? q.folderPath : null;
    const folderKey = typeof q.folderKey === "string" ? q.folderKey : null;

    // cancel any ongoing upload for this qid
    try {
      abortUploadRef.current[qid]?.abort();
    } catch {}
    const ctrl = new AbortController();
    abortUploadRef.current[qid] = ctrl;

    setUploadingByQid((prev) => ({ ...prev, [qid]: true }));
    setUploadErrByQid((prev) => ({ ...prev, [qid]: "" }));

    try {
      for (const file of arr) {
        const form = new FormData();
        form.append("file", file);

        // Link upload to DDQ question (for backend metadata)
        form.append("ddq_qid", String(qid));

        // folder hints per question
        if (folderPath) form.append("folder_path", JSON.stringify(folderPath));
        if (folderKey) form.append("folder_key", folderKey);

        // metadata
        form.append("original_name", file.name);

        const res = await request(uploadUrl(company), {
          method: "POST",
          body: form,
          formData: true,
          signal: ctrl.signal,
        });

        // Backend (DataRoomUploadView) wraps the result as
        //   { detail, uploaded, documents: [{ id, file: { url }, ... }] }
        // Older endpoints returned a flat object with id/doc_id, so we still
        // accept that shape for safety.
        const doc = Array.isArray(res?.documents) ? res.documents[0] : res;
        const docId = doc?.id ?? doc?.doc_id ?? null;
        const url = doc?.file?.url ?? doc?.url ?? doc?.file_url ?? null;
        const name = doc?.original_filename ?? doc?.title ?? doc?.name ?? doc?.filename ?? file.name;
        const size = doc?.file?.size ?? doc?.size ?? file.size;

        if (!docId) {
          throw new Error("Upload succeeded but response did not include a document id (id/doc_id).");
        }

        // Update UI state (store rich attachment object for UI)
        setState((prev) => {
          const current = prev.answers[qid] || emptyAnswer(qid);
          const existing = Array.isArray(current.attachments) ? current.attachments : [];

          const nextAtt = {
            docId,
            name,
            size,
            url,
            folderPath: folderPath || null,
            folderKey: folderKey || null,
            uploadedAt: new Date().toISOString(),
          };

          const requiresUpload = !!q.requiresUpload;
          const nextStatus =
            current.status === "todo" && requiresUpload ? "ok" : current.status;

          return {
            ...prev,
            answers: {
              ...prev.answers,
              [qid]: {
                ...current,
                status: nextStatus,
                attachments: [...existing, nextAtt],
              },
            },
          };
        });

        // Persist to backend DDQ model: attachments as [docId,...]
        // We patch attachments (and potentially status if we auto-set ok)
        const patch = {};
        if (q.requiresUpload) patch.status = "ok";
        patch.attachments = (prevAttachmentsFromState(state, qid) || []).concat([docId]);

        // The above `state` might be stale; safer: send only the new docId appended
        // by asking backend to merge attachments list. But backend merge is “replace”
        // within qid. So we send the full list we believe is correct from localStorage/state:
        const docIds = getDocIdsForQidFromLocalOrState(storageKey, company, qid);
        const nextDocIds = unique([...docIds, docId]);

        markSaving();
        try {
          await request(effectiveQuizzesUrl, {
            method: "POST",
            body: {
              ddq: {
                mode: "merge",
                answers: {
                  [qid]: {
                    ...(q.requiresUpload ? { status: "ok" } : {}),
                    attachments: nextDocIds,
                  },
                },
              },
            },
          });
          markSaveDone(true);
        } catch (saveErr) {
          markSaveDone(false, saveErr?.body?.detail || saveErr?.message);
          throw saveErr;
        }
      }
    } catch (err) {
      if (err?.name === "AbortError") return;

      const msg =
        err?.body?.detail ||
        err?.message ||
        "Kunde inte ladda upp filen just nu. Försök igen.";

      setUploadErrByQid((prev) => ({ ...prev, [qid]: msg }));
    } finally {
      setUploadingByQid((prev) => ({ ...prev, [qid]: false }));
    }
  };

  // --------------------------------------------------------
  // Remove file from UI + update backend DDQ attachments list
  // (server delete can be added later)
  // --------------------------------------------------------
  const handleFileRemove = async (qid, idx) => {
    let nextDocIds = null;

    setState((prev) => {
      const current = prev.answers[qid] || emptyAnswer(qid);
      const copy = [...(current.attachments || [])];
      copy.splice(idx, 1);

      nextDocIds = normalizeAttachmentsToDocIds(copy);

      return {
        ...prev,
        answers: {
          ...prev.answers,
          [qid]: { ...current, attachments: copy },
        },
      };
    });

    // Persist updated attachments list to backend
    if (company && Array.isArray(nextDocIds)) {
      markSaving();
      try {
        await request(effectiveQuizzesUrl, {
          method: "POST",
          body: {
            ddq: {
              mode: "merge",
              answers: {
                [qid]: { attachments: nextDocIds },
              },
            },
          },
        });
        markSaveDone(true);
      } catch (err) {
        markSaveDone(false, err?.body?.detail || err?.message);
      }
    }
  };

  // --------------------------------------------------------
  // AI autofill: call backend DDQAutoFillView
  // --------------------------------------------------------
  const handleAiSuggest = async (qid, questionText) => {
    if (!company) return;

    setAiByQid((prev) => ({ ...prev, [qid]: { loading: true, suggestion: null } }));

    try {
      const res = await request(
        `/users/workspaces/${encodeURIComponent(company)}/dataroom/ddq-autofill/`,
        {
          method: "POST",
          body: { qid, question_text: questionText },
        }
      );
      setAiByQid((prev) => ({ ...prev, [qid]: { loading: false, suggestion: res } }));
    } catch (err) {
      // Surface rate-limit clearly; fall back to generic message
      const isRateLimit = err?.status === 429 || err?.body?.status === 429;
      const msg = isRateLimit
        ? "Vänta lite innan du analyserar samma fråga igen."
        : err?.body?.detail || err?.message || "AI-förslag misslyckades.";
      setAiByQid((prev) => ({
        ...prev,
        [qid]: { loading: false, suggestion: { error: msg } },
      }));
    }
  };

  const handleAiAccept = (qid, suggestion) => {
    if (!suggestion) return;

    // Fix #1: compute the patch synchronously inside the setState updater,
    // which receives the current state directly — no stale localStorage reads.
    let patchToSend = null;

    setState((prev) => {
      const current = prev.answers[qid] || emptyAnswer(qid);
      const existing = Array.isArray(current.attachments) ? current.attachments : [];

      const existingIds = new Set(existing.map((a) => String(a?.docId ?? a)));
      const newAtts = (suggestion.sources || [])
        .filter((s) => s?.id != null && !existingIds.has(String(s.id)))
        .map((s) => ({ docId: s.id, name: s.name || `dok-${s.id}` }));

      const nextStatus = suggestion.status || current.status;
      const nextNotes = suggestion.notes || current.notes;
      const nextAttachments = [...existing, ...newAtts];

      // Capture exactly what we'll send — derived from the same prev state
      patchToSend = {
        status: nextStatus,
        notes: nextNotes,
        attachments: normalizeAttachmentsToDocIds(nextAttachments),
      };

      return {
        ...prev,
        answers: {
          ...prev.answers,
          [qid]: { ...current, status: nextStatus, notes: nextNotes, attachments: nextAttachments },
        },
      };
    });

    // patchToSend is set synchronously by the updater above
    if (patchToSend) {
      postDdqPatch(qid, patchToSend).catch(() => {});
    }

    setAiByQid((prev) => ({ ...prev, [qid]: { loading: false, suggestion: null } }));
  };

  const handleAiDismiss = (qid) => {
    setAiByQid((prev) => ({ ...prev, [qid]: { loading: false, suggestion: null } }));
  };

  return (
    <section className="ddq-section-root riskprofile-form">
      {/* Heading + intro renderas av DueDiligenceSection-wrappern i
          duediligence.js — duplicate här ledde till "DD-underlag" som
          rubrik två gånger i rad ovanför checklistan. */}
      {(loadingFromBackend || backendError) && (
        <header className="ddq-header">
          {loadingFromBackend && <p className="ddp-muted">Hämtar sparade svar…</p>}
          {backendError && <p className="questionnaire-error">{backendError}</p>}
        </header>
      )}

      <div className="ddq-layout-simple">
        {DDQ_SECTIONS.map((section) => (
          <DdqSection
            key={section.id}
            section={section}
            answers={state.answers}
            uploadingByQid={uploadingByQid}
            uploadErrByQid={uploadErrByQid}
            aiByQid={aiByQid}
            onQuestionChange={handleQuestionChange}
            onFilesAdd={handleFilesAdd}
            onFileRemove={handleFileRemove}
            onAiSuggest={handleAiSuggest}
            onAiAccept={handleAiAccept}
            onAiDismiss={handleAiDismiss}
          />
        ))}
      </div>

      {/* Floating save indicator (bottom-right). Silent when idle. */}
      <SaveStatusFloater status={saveStatus} />
    </section>
  );
}


// --------------------------------------------------------
// Floating save indicator — bottom-right toast that only renders during/
// after activity. Auto-hides 4 s after a successful save; sticks for errors
// until the next save kicks in.
// --------------------------------------------------------

function SaveStatusFloater({ status }) {
  const [visible, setVisible] = useState(false);
  const lastAtRef = useRef(0);

  useEffect(() => {
    if (!status) return;
    if (status.state === "saving" || status.state === "error") {
      setVisible(true);
      return;
    }
    if (status.state === "saved") {
      setVisible(true);
      const at = status.at ? status.at.getTime() : Date.now();
      lastAtRef.current = at;
      const t = setTimeout(() => {
        if (lastAtRef.current === at) setVisible(false);
      }, 4000);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [status?.state, status?.at]);

  if (!visible || !status || status.state === "idle") return null;

  const palette =
    status.state === "saving"
      ? { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8" }
      : status.state === "error"
      ? { bg: "#FEF2F2", border: "#FECACA", color: "#B91C1C" }
      : { bg: "#ECFDF5", border: "#A7F3D0", color: "#047857" };

  const label =
    status.state === "saving"
      ? "Sparar…"
      : status.state === "error"
      ? "Kunde inte spara"
      : "Sparat";

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 70,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 14px",
        borderRadius: 999,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        pointerEvents: "none",
        transition: "opacity 200ms ease",
      }}
      title={status.state === "error" ? status.msg || "" : ""}
    >
      {status.state === "saving" && (
        <span
          aria-hidden="true"
          style={{
            width: 8, height: 8, borderRadius: "50%",
            background: palette.color,
            animation: "pulse 1.4s ease-in-out infinite",
          }}
        />
      )}
      {status.state === "saved" && <span aria-hidden="true">✓</span>}
      {status.state === "error" && <span aria-hidden="true">⚠</span>}
      {label}
    </div>
  );
}


// --------------------------------------------------------
// Sections & cards
// --------------------------------------------------------

function DdqSection({
  section,
  answers,
  uploadingByQid,
  uploadErrByQid,
  aiByQid,
  onQuestionChange,
  onFilesAdd,
  onFileRemove,
  onAiSuggest,
  onAiAccept,
  onAiDismiss,
}) {
  return (
    <div className="ddq-section risk-card">
      <h3 className="ddq-section-title">
        {section.id}. {section.title}
      </h3>

      {section.items.map((q) => (
        <DdqQuestionCard
          key={q.qid}
          q={q}
          value={answers[q.qid] || emptyAnswer(q.qid)}
          uploading={!!uploadingByQid?.[q.qid]}
          uploadError={uploadErrByQid?.[q.qid] || ""}
          aiState={aiByQid?.[q.qid] || null}
          onChange={onQuestionChange}
          onFilesAdd={onFilesAdd}
          onFileRemove={onFileRemove}
          onAiSuggest={onAiSuggest}
          onAiAccept={onAiAccept}
          onAiDismiss={onAiDismiss}
        />
      ))}
    </div>
  );
}

function DdqQuestionCard({
  q,
  value,
  uploading,
  uploadError,
  aiState,
  onChange,
  onFilesAdd,
  onFileRemove,
  onAiSuggest,
  onAiAccept,
  onAiDismiss,
}) {
  const { qid, text, requiresUpload, folderPath } = q;
  const notAvail = value.status === "na";

  const aiLoading = aiState?.loading || false;
  const aiSuggestion = aiState?.suggestion || null;
  // no_docs = backend found zero documents for this qid (fix #2)
  const aiNoDocs = aiSuggestion?.no_docs === true;
  // real suggestion = has a status value and is not a no-docs/error response
  const aiHasSuggestion = aiSuggestion && !aiSuggestion.error && !aiNoDocs && aiSuggestion.status;

  return (
    <div className="ddq-card">
      <div className="ddq-card-head">
        <div className="ddq-qid">{qid}</div>
        <div className="ddq-qtext">{text}</div>
        <button
          type="button"
          className="ddq-ai-btn"
          onClick={() => onAiSuggest(qid, text)}
          disabled={aiLoading}
          title="Analysera uppladdade dokument och föreslå svar"
        >
          {aiLoading ? "Analyserar…" : "✦ Föreslå från dokument"}
        </button>
      </div>

      {/* Fix #2: explicit no-docs message */}
      {aiNoDocs && (
        <div className="ddq-ai-nodocs">
          {aiSuggestion.message || "Inga dokument hittades för denna fråga."}
          <button type="button" className="ddq-ai-dismiss-inline" onClick={() => onAiDismiss(qid)}>
            ✕
          </button>
        </div>
      )}

      {/* Fix #12: uses module-level CONFIDENCE_LABEL / DDQ_STATUS_LABEL */}
      {aiHasSuggestion && (
        <div className="ddq-ai-suggestion">
          <div className="ddq-ai-suggestion-header">
            <span className="ddq-ai-label">AI-förslag</span>
            {aiSuggestion.confidence && aiSuggestion.confidence !== "none" && (
              <span className={`ddq-ai-confidence ddq-ai-confidence--${aiSuggestion.confidence}`}>
                Säkerhet: {CONFIDENCE_LABEL[aiSuggestion.confidence] || aiSuggestion.confidence}
              </span>
            )}
            {aiSuggestion.doc_count != null && (
              <span className="ddq-ai-docsused">
                {aiSuggestion.doc_count} dokument analyserade
              </span>
            )}
          </div>

          <div className="ddq-ai-row">
            <span className="ddq-ai-field">Föreslagen status:</span>
            <span className={`ddq-ai-status-badge ddq-ai-status--${aiSuggestion.status}`}>
              {DDQ_STATUS_LABEL[aiSuggestion.status] || aiSuggestion.status}
            </span>
          </div>

          {aiSuggestion.notes && (
            <div className="ddq-ai-row">
              <span className="ddq-ai-field">Kommentar:</span>
              <span className="ddq-ai-notes">{aiSuggestion.notes}</span>
            </div>
          )}

          {Array.isArray(aiSuggestion.sources) && aiSuggestion.sources.length > 0 && (
            <div className="ddq-ai-row">
              <span className="ddq-ai-field">Baserat på:</span>
              <span className="ddq-ai-sources">
                {aiSuggestion.sources.map((s) => s.name || `dok-${s.id}`).join(", ")}
              </span>
            </div>
          )}

          <div className="ddq-ai-actions">
            <button
              type="button"
              className="ddq-ai-accept"
              onClick={() => onAiAccept(qid, aiSuggestion)}
            >
              Acceptera
            </button>
            <button
              type="button"
              className="ddq-ai-dismiss"
              onClick={() => onAiDismiss(qid)}
            >
              Avvisa
            </button>
          </div>
        </div>
      )}

      {aiSuggestion?.error && (
        <div className="ddq-ai-error">{aiSuggestion.error}</div>
      )}

      <div className="ddq-card-body">
        {requiresUpload && Array.isArray(folderPath) && folderPath.length > 0 && (
          <div className="ddq-row">
            <label className="ddq-label">Lägg filen i dataroom</label>
            <div className="ddq-folderhint">{folderPath.join(" / ")}</div>
          </div>
        )}

        <div className="ddq-row">
          <label className="ddq-label">Status</label>
          <div className="ddq-status">
            <label className="ddq-radio">
              <input
                type="radio"
                name={`st-${qid}`}
                checked={value.status === "ok"}
                onChange={() => onChange(qid, { status: "ok" })}
              />
              Finns / klar
            </label>
            <label className="ddq-radio">
              <input
                type="radio"
                name={`st-${qid}`}
                checked={value.status === "na"}
                onChange={() => onChange(qid, { status: "na" })}
              />
              Ej tillgängligt
            </label>
            <label className="ddq-radio">
              <input
                type="radio"
                name={`st-${qid}`}
                checked={value.status === "todo"}
                onChange={() => onChange(qid, { status: "todo" })}
              />
              Pågår
            </label>
          </div>
        </div>

        <div className="ddq-row">
          <label className="ddq-label">Kommentar / förklaring</label>
          <textarea
            rows={3}
            placeholder={
              notAvail
                ? "Förklara kort varför uppgiften inte finns tillgänglig."
                : "Lägg till ev. kommentarer."
            }
            value={value.notes}
            onChange={(e) => onChange(qid, { notes: e.target.value })}
          />
        </div>

        <div className="ddq-row">
          <label className="ddq-label">
            Bilagor {requiresUpload ? "(efterfrågat)" : "(valfritt)"}
          </label>

          <div>
            <div className="ddq-upload">
              <input
                type="file"
                multiple
                disabled={uploading}
                onChange={(e) => {
                  onFilesAdd(qid, e.target.files);
                  e.target.value = "";
                }}
              />
              {uploading && <span className="ddq-uploading">Laddar upp…</span>}
            </div>

            {uploadError && <p className="questionnaire-error">{uploadError}</p>}

            {Array.isArray(value.attachments) && value.attachments.length > 0 && (
              <ul className="ddq-files">
                {value.attachments.map((att, idx) => (
                  <li key={att?.docId || `${att?.name || "file"}-${idx}`} className="ddq-file">
                    {att?.url ? (
                      <a href={att.url} target="_blank" rel="noreferrer">
                        {att.name || `Dokument ${att.docId}`}
                      </a>
                    ) : (
                      <span>{att?.name || `Dokument ${att?.docId}`}</span>
                    )}

                    <span className="ddq-file-meta">{formatBytes(att?.size)}</span>

                    <button
                      type="button"
                      className="ddq-link-danger"
                      onClick={() => onFileRemove(qid, idx)}
                      disabled={uploading}
                    >
                      Ta bort
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------
// State helpers
// --------------------------------------------------------

function emptyAnswer(qid) {
  return { qid, status: "todo", notes: "", attachments: [] };
}

function createInitialDdqState(company) {
  return {
    company: company || "",
    answers: {},
    version: 1,
    createdAt: new Date().toISOString(),
  };
}

function loadDdqFromStorage(storageKey, company) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return createInitialDdqState(company);

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return createInitialDdqState(company);

    Object.values(parsed.answers || {}).forEach((a) => {
      if (!Array.isArray(a.attachments)) a.attachments = [];
    });

    return { ...createInitialDdqState(company), ...parsed };
  } catch {
    return createInitialDdqState(company);
  }
}

function saveDdqToStorage(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function buildDdqSummary(state) {
  let total = 0;
  let completed = 0;
  let attachments = 0;

  DDQ_SECTIONS.forEach((s) =>
    (s.items || []).forEach((q) => {
      total += 1;
      const a = state.answers?.[q.qid];
      if (a && (a.status === "ok" || a.status === "na")) completed += 1;
      const att = a?.attachments;
      if (Array.isArray(att)) attachments += att.length;
    })
  );

  return { total, completed, attachments };
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

// --------------------------------------------------------
// Backend/UI answer bridging helpers
// --------------------------------------------------------

function mergeBackendAnswersIntoUiState(baseState, backendAnswers) {
  const next = { ...baseState, answers: { ...(baseState.answers || {}) } };

  Object.entries(backendAnswers || {}).forEach(([qid, raw]) => {
    const r = raw && typeof raw === "object" ? raw : {};
    const status = r.status || "todo";
    const notes = r.notes || "";

    // backend attachments might be [docId,...]
    const atts = Array.isArray(r.attachments)
      ? r.attachments.map((docId) => ({ docId }))
      : [];

    next.answers[qid] = {
      ...(next.answers[qid] || emptyAnswer(qid)),
      status,
      notes,
      attachments: atts,
    };
  });

  return next;
}

function mergeUiAnswersPreferRich(oldAnswers, newAnswers) {
  const out = { ...(newAnswers || {}) };

  Object.keys(out).forEach((qid) => {
    const oldA = oldAnswers?.[qid];
    const newA = out[qid];
    if (!oldA || !newA) return;

    // If old has rich attachment objects (name/url/size), keep that.
    const oldAtt = Array.isArray(oldA.attachments) ? oldA.attachments : [];
    const newAtt = Array.isArray(newA.attachments) ? newA.attachments : [];

    const oldById = {};
    oldAtt.forEach((x) => {
      const id = x?.docId;
      if (id != null) oldById[String(id)] = x;
    });

    const mergedAtt = newAtt.map((x) => {
      const id = x?.docId;
      if (id == null) return x;
      return oldById[String(id)] || x;
    });

    out[qid] = { ...newA, attachments: mergedAtt };
  });

  // Also keep answers that exist locally but not yet on backend
  Object.entries(oldAnswers || {}).forEach(([qid, a]) => {
    if (!out[qid]) out[qid] = a;
  });

  return out;
}

function normalizeAttachmentsToDocIds(attachments) {
  const arr = Array.isArray(attachments) ? attachments : [];
  const ids = [];
  for (const a of arr) {
    if (typeof a === "number") ids.push(a);
    else if (typeof a === "string" && a.trim() !== "") ids.push(a);
    else if (a && typeof a === "object" && a.docId != null) ids.push(a.docId);
  }
  // ensure stable unique list
  return unique(ids);
}

function unique(arr) {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const k = String(x);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

// These two helpers are used to avoid stale state when persisting attachments
function getDocIdsForQidFromLocalOrState(storageKey, company, qid) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const a = parsed?.answers?.[qid];
    return normalizeAttachmentsToDocIds(a?.attachments || []);
  } catch {
    return [];
  }
}

// (kept for completeness; not used in final patch logic)
function prevAttachmentsFromState(state, qid) {
  try {
    const a = state?.answers?.[qid];
    return normalizeAttachmentsToDocIds(a?.attachments || []);
  } catch {
    return [];
  }
}
